/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable no-inner-declarations */
import { FieldType } from '../../Model';
import { Channel } from '../Channel';
import { FFuncType } from '../Formula/FormulaFunc';
import { VariableType } from '../Field';
import { SelectionMode } from '../Filter';
export class PivotFilterType {
    constructor() {
        this._baseFilters = new Map();
        this._layerFilters = new Map();
    }
    get baseFilters() {
        return this._baseFilters;
    }
    get layerFilters() {
        return this._layerFilters;
    }
    getBaseFilter(filterName) {
        return this.baseFilters.get(filterName);
    }
    getLayerFilter(layerName, filterName) {
        const layerFilters = this.layerFilters.get(layerName);
        if (layerFilters !== undefined) {
            return layerFilters.get(filterName);
        }
    }
    addBaseFilter(filterName, filter) {
        if (!this.baseFilters.has(filterName)) {
            this.baseFilters.set(filterName, filter);
        }
        return this;
    }
    addLayerFilter(layerName, filterName, filter) {
        let layerFilters = this.layerFilters.get(layerName);
        if (layerFilters == null) {
            layerFilters = new Map();
            this.layerFilters.set(layerName, layerFilters);
        }
        if (!layerFilters.has(filterName)) {
            layerFilters.set(filterName, filter);
        }
        return this;
    }
}
export var PivotFilter;
(function (PivotFilter) {
    function makeCollectFilter(model, query) {
        let filters = new Array();
        const valueFunc = (field, row) => {
            if (field.type === FieldType.VIEWCALC && field.formula.expr.type === FFuncType.SCALAR) {
                return field.formula.expr.value({ row });
            }
            else {
                return null;
            }
        };
        filters = addCollectFilters(filters, model, query);
        return makeCombinedCollectFilterFunc(filters, model, valueFunc);
    }
    PivotFilter.makeCollectFilter = makeCollectFilter;
    function addCollectFilters(collectFilters, model, query) {
        let selected = collectFilters.slice();
        for (const [, filter] of Array.from(query.filters)) {
            switch (filter.field.type) {
                case FieldType.VIEWCALC:
                    if (filter.field.isDimension) {
                        selected = selected.concat(filter);
                    }
            }
        }
        return selected;
    }
    function makeCombinedCollectFilterFunc(filters, model, valueFunc) {
        let filterFunc = null;
        return (row) => {
            for (const filter of filters) {
                filterFunc = makeFilterFunc(model, filter, valueFunc);
                if (!filterFunc(row)) {
                    return false;
                }
            }
            return true;
        };
    }
    /**
     * Reduce Filters
     */
    function makeReduceFilter(model, query) {
        let pivotFilter = new PivotFilterType();
        pivotFilter = addBaseFilters(pivotFilter, model, query, query.rows.fields);
        pivotFilter = addBaseFilters(pivotFilter, model, query, query.columns.fields);
        for (const layer of getLayers(query)) {
            pivotFilter = addLayerFilters(pivotFilter, model, query, query.rows.fields, layer.name);
            pivotFilter = addLayerFilters(pivotFilter, model, query, query.columns.fields, layer.name);
            pivotFilter = addMarkFilters(pivotFilter, model, query, layer);
        }
        return pivotFilter;
    }
    PivotFilter.makeReduceFilter = makeReduceFilter;
    function executeReduceFilter(model, result, pivotFilter) {
        const valueFunc = (field, row) => {
            if (field.type === FieldType.VIEWCALC) {
                return row[field.alias];
            }
            else {
                return row['Measure Names'];
            }
        };
        // Filter Base
        const baseFilter = makeCombinedFilterFunc(model, pivotFilter.baseFilters, valueFunc);
        if (baseFilter != null) {
            result.base.data = result.base.data.filter(baseFilter);
        }
        // Filter Layers
        for (const [layerName, layerFilters] of Array.from(pivotFilter.layerFilters)) {
            const layer = result.getResultSet(layerName);
            const layerFilter = makeCombinedFilterFunc(model, layerFilters, valueFunc);
            if (layer != null && layerFilter != null) {
                layer.data = layer.data.filter(layerFilter);
            }
        }
        return result;
    }
    PivotFilter.executeReduceFilter = executeReduceFilter;
    function addBaseFilters(pivotFilter, model, query, fields) {
        for (const field of fields) {
            switch (field.type) {
                case FieldType.VIEWCALC:
                    if (field.isDimension) {
                        break;
                    }
                // eslint-disable-next-line no-fallthrough
                case FieldType.VIEWNAMES:
                    if (query.filters.has(field.name)) {
                        const filter = query.filters.get(field.name);
                        pivotFilter = pivotFilter.addBaseFilter(filter.field.name, filter);
                    }
            }
        }
        return pivotFilter;
    }
    function addLayerFilters(pivotFilter, model, query, fields, layerName) {
        for (const field of fields) {
            switch (field.type) {
                case FieldType.VIEWCALC:
                    if (field.isDimension) {
                        break;
                    }
                // eslint-disable-next-line no-fallthrough
                case FieldType.VIEWNAMES:
                    if (query.filters.has(field.name)) {
                        const filter = query.filters.get(field.name);
                        pivotFilter = pivotFilter.addLayerFilter(layerName, filter.field.name, filter);
                    }
            }
        }
        return pivotFilter;
    }
    function addMarkFilters(pivotFilter, model, query, layer) {
        return [
            Channel.ROW,
            Channel.COLUMN,
            Channel.VALUE,
            Channel.COLOR,
            Channel.SIZE,
            Channel.TEXT,
            Channel.DETAIL,
            Channel.TOOLTIP,
            Channel.ANGLE,
            Channel.PATH,
        ].reduce((filters, channel) => {
            return addLayerFilters(filters, model, query, layer.fields.get(channel) || [], layer.name);
        }, pivotFilter);
    }
    function getLayers(query) {
        if (query.layers.get(Channel.STAR).length > 0) {
            return query.layers.get(Channel.STAR);
        }
        if (query.layers.get(Channel.ROW).length > 0) {
            return query.layers.get(Channel.ROW);
        }
        if (query.layers.get(Channel.COLUMN).length > 0) {
            return query.layers.get(Channel.COLUMN);
        }
        return new Array();
    }
    /**
     * Filter Funcs
     */
    function makeFilterFunc(model, filter, valueFunc) {
        switch (filter.varType) {
            case VariableType.DISCRETE:
                return makeDiscreteFilterFunc(filter, valueFunc);
            case VariableType.CONTINUOUS:
                return makeContinuousFilterFunc(filter, valueFunc);
        }
    }
    function makeDiscreteFilterFunc(filter, valueFunc) {
        const selected = filter.selection.selected;
        const inclusionMode = filter.selection.mode;
        return (row) => {
            const value = valueFunc(filter.field, row);
            switch (inclusionMode) {
                case SelectionMode.ALL:
                    return true;
                case SelectionMode.INCLUDE:
                    if (selected.has(value)) {
                        return true;
                    }
                    else {
                        return false;
                    }
                case SelectionMode.EXCLUDE:
                    if (selected.has(value)) {
                        return false;
                    }
                    else {
                        return true;
                    }
            }
        };
    }
    function makeContinuousFilterFunc(filter, valueFunc) {
        const range = filter.range;
        const minValue = range.min;
        const maxValue = range.max;
        let minFilter;
        let maxFilter;
        if (minValue === undefined || minValue === null || minValue === '') {
            minFilter = () => {
                return true;
            };
        }
        else if (range.includeMin) {
            minFilter = (value) => {
                return value >= minValue;
            };
        }
        else {
            minFilter = (value) => {
                return value > minValue;
            };
        }
        if (maxValue === undefined || maxValue === null || maxValue === '') {
            maxFilter = () => {
                return true;
            };
        }
        else if (range.includeMax) {
            maxFilter = (value) => {
                return value <= maxValue;
            };
        }
        else {
            maxFilter = (value) => {
                return value < maxValue;
            };
        }
        return (row) => {
            const value = valueFunc(filter.field, row);
            if (value == null) {
                return range.includeNulls;
            }
            else {
                return minFilter(value) && maxFilter(value);
            }
        };
    }
    // General Helpers
    function makeCombinedFilterFunc(model, filters, valueFunc) {
        let filterFunc = null;
        for (const [, filter] of Array.from(filters)) {
            if (filterFunc == null) {
                filterFunc = makeFilterFunc(model, filter, valueFunc);
            }
            else {
                filterFunc = (row) => {
                    return filterFunc(row) && makeFilterFunc(model, filter, valueFunc)(row);
                };
            }
        }
        return filterFunc || (() => true);
    }
})(PivotFilter || (PivotFilter = {}));
